if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.desktopMql = window.matchMedia('(min-width: 990px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 300));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');
            if (!button) return;

            button.addEventListener(
              'click',
              this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
            );
          });
        }

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.desktopMql.matches) {
          this.removeListSemantic();
        }

        /*
          Trinity Press:
          Make sure the media item already marked active by Liquid is the one
          displayed when the gallery initializes. This is especially useful
          when the selected variant's featured image is not the first product image.
        */
        const initialActiveMedia = this.elements.viewer?.querySelector('[data-media-id].is-active');
        if (initialActiveMedia) {
          this.setActiveThumbnail(
            this.elements.thumbnails?.querySelector(`[data-target="${initialActiveMedia.dataset.mediaId}"]`)
          );
        }

        this.setupThumbnailNavigationButtons();
        this.setupThumbnailOverflowFades();
      }

      getThumbnailSlider() {
        return this.elements.thumbnails?.slider || this.elements.thumbnails?.querySelector('.thumbnail-list');
      }

      setupThumbnailNavigationButtons() {
        if (!this.elements.thumbnails) return;

        const rail = this.elements.thumbnails;

        if (!rail.querySelector('.tp-thumbnail-nav--up')) {
          const upButton = document.createElement('button');
          upButton.type = 'button';
          upButton.className = 'tp-thumbnail-nav tp-thumbnail-nav--up';
          upButton.setAttribute('aria-label', 'Show previous product images');
          upButton.innerHTML = '<span class="tp-thumbnail-nav__icon" aria-hidden="true"></span>';
          upButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.scrollThumbnailRail(-1);
          });
          rail.appendChild(upButton);
        }

        if (!rail.querySelector('.tp-thumbnail-nav--down')) {
          const downButton = document.createElement('button');
          downButton.type = 'button';
          downButton.className = 'tp-thumbnail-nav tp-thumbnail-nav--down';
          downButton.setAttribute('aria-label', 'Show more product images');
          downButton.innerHTML = '<span class="tp-thumbnail-nav__icon" aria-hidden="true"></span>';
          downButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.scrollThumbnailRail(1);
          });
          rail.appendChild(downButton);
        }
      }

      scrollThumbnailRail(direction) {
        const thumbnailSlider = this.getThumbnailSlider();
        if (!thumbnailSlider || !this.desktopMql.matches) return;

        const firstItem = thumbnailSlider.querySelector('.thumbnail-list__item');
        if (!firstItem) return;

        const styles = window.getComputedStyle(thumbnailSlider);
        const gap = parseFloat(styles.rowGap || styles.gap || '0') || 0;
        const step = firstItem.getBoundingClientRect().height + gap;
        const maxScrollTop = Math.max(0, thumbnailSlider.scrollHeight - thumbnailSlider.clientHeight);

        const targetTop = Math.min(
          maxScrollTop,
          Math.max(0, thumbnailSlider.scrollTop + direction * step)
        );

        thumbnailSlider.scrollTo({
          top: targetTop,
          behavior: 'smooth',
        });

        window.setTimeout(() => this.normalizeThumbnailScrollEdges(), 260);
      }

      setupThumbnailOverflowFades() {
        if (!this.elements.thumbnails) return;

        const thumbnailSlider = this.getThumbnailSlider();
        if (!thumbnailSlider) return;

        const update = () => this.updateThumbnailOverflowFades();
        thumbnailSlider.addEventListener('scroll', update, { passive: true });

        /*
          Keep desktop wheel / trackpad movement inside the thumbnail rail.
          We clamp the requested position ourselves so Safari cannot rubber-band
          the rail a few pixels past either end and visually clip the first or
          last thumbnail before bouncing back.
        */
        thumbnailSlider.addEventListener(
          'wheel',
          (event) => {
            if (!this.desktopMql.matches) return;

            const maxScrollTop = Math.max(0, thumbnailSlider.scrollHeight - thumbnailSlider.clientHeight);
            if (maxScrollTop <= 0) return;

            event.preventDefault();

            const nextTop = Math.min(
              maxScrollTop,
              Math.max(0, thumbnailSlider.scrollTop + event.deltaY)
            );

            thumbnailSlider.scrollTop = nextTop;
            this.updateThumbnailOverflowFades();
          },
          { passive: false }
        );

        const normalizeEdges = () => this.normalizeThumbnailScrollEdges();

        if ('onscrollend' in window) {
          thumbnailSlider.addEventListener('scrollend', normalizeEdges, { passive: true });
        } else {
          let scrollEndTimer;
          thumbnailSlider.addEventListener(
            'scroll',
            () => {
              window.clearTimeout(scrollEndTimer);
              scrollEndTimer = window.setTimeout(normalizeEdges, 90);
            },
            { passive: true }
          );
        }

        if (typeof this.desktopMql.addEventListener === 'function') {
          this.desktopMql.addEventListener('change', update);
        }

        window.addEventListener('resize', update, { passive: true });

        if ('ResizeObserver' in window) {
          this.thumbnailResizeObserver = new ResizeObserver(update);
          this.thumbnailResizeObserver.observe(thumbnailSlider);
        }

        requestAnimationFrame(() => {
          this.normalizeThumbnailScrollEdges();
          update();
        });
      }

      normalizeThumbnailScrollEdges() {
        const thumbnailSlider = this.getThumbnailSlider();
        if (!thumbnailSlider || !this.desktopMql.matches) return;

        const maxScrollTop = Math.max(0, thumbnailSlider.scrollHeight - thumbnailSlider.clientHeight);
        const edgeSnapTolerance = 8;

        if (thumbnailSlider.scrollTop <= edgeSnapTolerance) {
          thumbnailSlider.scrollTop = 0;
        } else if (maxScrollTop - thumbnailSlider.scrollTop <= edgeSnapTolerance) {
          thumbnailSlider.scrollTop = maxScrollTop;
        }

        this.updateThumbnailOverflowFades();
      }

      updateThumbnailOverflowFades() {
        if (!this.elements.thumbnails) return;

        const rail = this.elements.thumbnails;
        const thumbnailSlider = this.getThumbnailSlider();

        if (!thumbnailSlider || !this.desktopMql.matches) {
          rail.classList.remove('tp-can-scroll-up', 'tp-can-scroll-down');
          return;
        }

        const maxScrollTop = Math.max(0, thumbnailSlider.scrollHeight - thumbnailSlider.clientHeight);

        if (maxScrollTop <= 4) {
          rail.classList.remove('tp-can-scroll-up', 'tp-can-scroll-down');
          return;
        }

        /*
          Use the rail's actual scroll range with a wider edge tolerance.
          This makes the fade/buttons switch OFF before browser sub-pixel
          rounding can make the first/last thumbnail look partially masked.
        */
        const edgeTolerance = 8;
        const currentTop = Math.min(maxScrollTop, Math.max(0, thumbnailSlider.scrollTop));

        const hasHiddenAbove = currentTop > edgeTolerance;
        const hasHiddenBelow = currentTop < maxScrollTop - edgeTolerance;

        rail.classList.toggle('tp-can-scroll-up', hasHiddenAbove);
        rail.classList.toggle('tp-can-scroll-down', hasHiddenBelow);
      }

      onSlideChanged(event) {
        const mediaId = event.detail?.currentElement?.dataset?.mediaId;
        if (!mediaId || !this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend = false) {
        if (!this.elements.viewer) return;

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        activeMedia.classList.add('is-active');

        /*
          Dawn normally moves variant media to the front of the gallery when
          prepend=true. Trinity Press intentionally keeps the thumbnail rail in
          its stable visual order, so prepend is accepted for compatibility but
          not used to reorder media.
        */
        void prepend;

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.desktopMql.matches) {
            const viewerSlider = this.elements.viewer.slider || this.elements.viewer.querySelector('.slider');
            if (viewerSlider) {
              viewerSlider.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
            }
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => {
          element.removeAttribute('aria-current');
        });

        const button = thumbnail.querySelector('button');
        if (button) button.setAttribute('aria-current', 'true');

        const thumbnailSlider = this.getThumbnailSlider();

        if (!thumbnailSlider) return;

        if (this.desktopMql.matches) {
          const itemTop = thumbnail.offsetTop;
          const itemBottom = itemTop + thumbnail.offsetHeight;
          const visibleTop = thumbnailSlider.scrollTop;
          const visibleBottom = visibleTop + thumbnailSlider.clientHeight;

          if (itemTop < visibleTop) {
            thumbnailSlider.scrollTo({ top: itemTop, behavior: 'smooth' });
          } else if (itemBottom > visibleBottom) {
            thumbnailSlider.scrollTo({ top: itemBottom - thumbnailSlider.clientHeight, behavior: 'smooth' });
          }
        } else {
          const itemLeft = thumbnail.offsetLeft;
          const itemRight = itemLeft + thumbnail.offsetWidth;
          const visibleLeft = thumbnailSlider.scrollLeft;
          const visibleRight = visibleLeft + thumbnailSlider.clientWidth;

          if (itemLeft < visibleLeft) {
            thumbnailSlider.scrollTo({ left: itemLeft, behavior: 'smooth' });
          } else if (itemRight > visibleRight) {
            thumbnailSlider.scrollTo({ left: itemRight - thumbnailSlider.clientWidth, behavior: 'smooth' });
          }
        }

        this.updateThumbnailOverflowFades();
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;

        const image = activeItem.querySelector('.product__media img');
        if (!image) return;

        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', 'false');

          if (window.accessibilityStrings?.imageAvailable) {
            this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          }

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', 'true');
          }, 2000);
        };

        if (image.complete) {
          announce();
        } else {
          image.addEventListener('load', announce, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        if (typeof window.pauseAllMedia === 'function') {
          window.pauseAllMedia();
        }

        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;

        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems?.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
